import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import { queryClient } from '@/lib/query-client';
import type { WorkflowAuditEntryResponse } from '@/lib/api';
import type { WorkflowActionSSEEvent } from '@/lib/workflow-audit';
import type { WorkflowAuditEvent } from '@/lib/types';
import { mergeWorkflowAuditEntries, normalizeWorkflowActionEvent } from '@/lib/workflow-audit';

interface WorkflowAuditStoreState {
  entries: WorkflowAuditEntryResponse[];
  handleWorkflowAudit: (entry: WorkflowAuditEntryResponse) => void;
  handleWorkflowAction: (event: WorkflowActionSSEEvent) => void;
  clearWorkflowAuditEntries: () => void;
}

function invalidateWorkflowAuditQueries(): void {
  queryClient.invalidateQueries({ queryKey: ['workflowAuditFeed'] }).catch((err: unknown) => {
    console.warn('[WorkflowAuditStore] Failed to invalidate audit feed cache', {
      error: err instanceof Error ? err.message : err,
    });
  });
}

export const useWorkflowAuditStore = create<WorkflowAuditStoreState>()(
  devtools(
    subscribeWithSelector(set => ({
      entries: [],

      handleWorkflowAudit: (entry: WorkflowAuditEntryResponse): void => {
        set(
          state => ({
            entries: mergeWorkflowAuditEntries(state.entries, [entry]).slice(0, 25),
          }),
          undefined,
          'workflowAudit/workflowAudit'
        );

        invalidateWorkflowAuditQueries();
      },

      handleWorkflowAction: (event: WorkflowActionSSEEvent): void => {
        const normalized = normalizeWorkflowActionEvent(event);
        if (!normalized) return;

        set(
          state => {
            return {
              entries: mergeWorkflowAuditEntries(state.entries, [normalized]).slice(0, 25),
            };
          },
          undefined,
          'workflowAudit/workflowAction'
        );

        invalidateWorkflowAuditQueries();
      },

      clearWorkflowAuditEntries: (): void => {
        set(() => ({ entries: [] }), undefined, 'workflowAudit/clear');
      },
    })),
    { name: 'WorkflowAuditStore', enabled: import.meta.env.DEV }
  )
);

const { handleWorkflowAudit, handleWorkflowAction, clearWorkflowAuditEntries } =
  useWorkflowAuditStore.getState();

export const workflowAuditSSEHandlers = {
  onWorkflowAudit: (event: WorkflowAuditEvent): void => {
    const { type, ...entry } = event;
    // Discard type field; handleWorkflowAudit uses the entry fields
    void type;
    handleWorkflowAudit(entry);
  },
  onWorkflowAction: handleWorkflowAction,
} as const;

export function cleanupWorkflowAuditStore(): void {
  clearWorkflowAuditEntries();
}
